import React, { useState, useEffect } from "react"
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Spin,
  Alert,
  InputNumber,
  Grid,
  Divider,
} from "antd"
import {
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  MailOutlined,
  SaveOutlined,
  BuildOutlined,
  ExpandOutlined,
  NumberOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TeamOutlined,
  BankOutlined,
  ApartmentOutlined,
} from "@ant-design/icons"
import { Provinces } from "common/data/Provinces"
import { Cities } from "common/data/Cities"
import { Section } from "common/data/Section"

const { Text, Title } = Typography
const { TextArea } = Input
const { useBreakpoint } = Grid

export const ProjectUpdate = ({ rowData, mainProps, openNotification }) => {
  const { onUpdateGasProject, loading, t } = mainProps
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [form] = Form.useForm()
  const [cityList, setCityList] = useState([])
  const [sectionList, setSectionList] = useState([])
  const [selectedProvince, setSelectedProvince] = useState(13)
  const [selectedCity, setSelectedCity] = useState(null)
  const [selectedSection, setSelectedSection] = useState(null)

  // Province list
  const provinceOptions = Provinces.map(p => ({
    label: p.ProvinceName,
    value: p.Id,
  }))

  // Initialize form and location data
  useEffect(() => {
    if (rowData) {
      // Set form values
      form.setFieldsValue({
        landlordName: rowData.landlordName,
        companyName: rowData.companyName,
        landlordNaCode: rowData.landlordNaCode,
        landlordPhoneNumber: rowData.landlordPhoneNumber,
        postalCode: rowData.postalCode,
        area: rowData.area,
        address: rowData.address,
        floorNumber: rowData.floorNumber,
        buildingTypeEnum: rowData.buildingTypeEnum,
        des1: rowData.des1,
      })

      // Initialize location based on idSection
      if (rowData.idSection) {
        const sectionSelected = Section.find(s => s.Id === rowData.idSection)
        if (sectionSelected) {
          const citySelected = Cities.find(c => c.Id === sectionSelected.Id_City)
          if (citySelected) {
            // Set province
            setSelectedProvince(citySelected.Id_Province)

            // Load cities for province
            const newCityList = Cities.filter(c => c.Id_Province === citySelected.Id_Province)
            setCityList(newCityList.map(c => ({ label: c.CityName, value: c.Id })))

            // Set city
            setSelectedCity(citySelected.Id)

            // Load sections for city
            const newSectionList = Section.filter(s => s.Id_City === citySelected.Id)
            setSectionList(newSectionList.map(s => ({ label: s.SectionName, value: s.Id })))

            // Set section
            setSelectedSection(rowData.idSection)
          }
        }
      } else {
        // Default to Kurdistan province
        const defaultCities = Cities.filter(c => c.Id_Province === 13)
        setCityList(defaultCities.map(c => ({ label: c.CityName, value: c.Id })))
      }
    }
  }, [rowData, form])

  // Handle province change
  const handleProvinceChange = value => {
    setSelectedProvince(value)
    setSelectedCity(null)
    setSelectedSection(null)
    setSectionList([])

    const newCityList = Cities.filter(c => c.Id_Province === value)
    setCityList(newCityList.map(c => ({ label: c.CityName, value: c.Id })))
  }

  // Handle city change
  const handleCityChange = value => {
    setSelectedCity(value)
    setSelectedSection(null)

    const newSectionList = Section.filter(s => s.Id_City === value)
    setSectionList(newSectionList.map(s => ({ label: s.SectionName, value: s.Id })))

    // Auto-select "مرکزی" if available
    const centerSection = newSectionList.find(s => s.SectionName === "مرکزی")
    if (centerSection) {
      setSelectedSection(centerSection.Id)
    }
  }

  // Handle section change
  const handleSectionChange = value => {
    setSelectedSection(value)
  }

  // Handle form submit
  const handleSubmit = async values => {
    const dataToSave = {
      ...values,
      id: rowData.id,
      buildingTypeEnum: +values.buildingTypeEnum,
      IdSection: selectedSection || rowData.idSection,
    }

    await onUpdateGasProject(dataToSave)
  }

  // Check if editing is allowed
  const isEditable = rowData?.projectLevelEnum === 0

  // Get building type label
  const getBuildingTypeLabel = (type) => {
    const types = { 1: "مسکونی", 2: "تجاری", 3: "اداری", 5: "عمومی" }
    return types[type] || "-"
  }

  // Get location names
  const getLocationNames = () => {
    const section = Section.find(s => s.Id === rowData?.idSection)
    const city = section ? Cities.find(c => c.Id === section.Id_City) : null
    const province = city ? Provinces.find(p => p.Id === city.Id_Province) : null
    return {
      section: section?.SectionName || "-",
      city: city?.CityName || "-",
      province: province?.ProvinceName || "-",
    }
  }

  // Detail Item Component for read-only view
  const DetailItem = ({ icon, label, value, color, isTag = false }) => (
    <div
      style={{
        padding: "12px 16px",
        background: "#fafafa",
        borderRadius: 8,
        border: "1px solid #f0f0f0",
        height: "100%",
      }}
    >
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {icon && <span style={{ marginLeft: 6 }}>{icon}</span>}
          {label}
        </Text>
        {isTag ? (
          <Tag color={color} style={{ fontSize: 13, padding: "2px 8px" }}>
            {value || "-"}
          </Tag>
        ) : (
          <Text strong style={{ fontSize: 14, color: color || "#262626" }}>
            {value || "-"}
          </Text>
        )}
      </Space>
    </div>
  )

  // Read-only view when not editable
  if (!isEditable) {
    const location = getLocationNames()
    return (
      <div>
        {/* Header Card */}
        <Card
          size="small"
          style={{
            marginBottom: 16,
            borderRadius: 12,
            border: "none",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
          }}
        >
          <Row gutter={[16, 8]} align="middle">
            <Col flex="auto">
              <Space align="center" size={12}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileTextOutlined style={{ fontSize: 24, color: "#fff" }} />
                </div>
                <div>
                  <Title level={4} style={{ margin: 0, color: "#fff" }}>
                    مشخصات پرونده
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>
                    شماره پرونده: {rowData?.projectNumber || "-"}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Tag
                color="orange"
                style={{
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                فقط خواندنی
              </Tag>
            </Col>
          </Row>
        </Card>

        {/* Alert */}
        <Alert
          message="امکان ویرایش وجود ندارد"
          description="پرونده در مرحله‌ای است که امکان ویرایش اطلاعات وجود ندارد. جهت نیاز به تغییر مشخصات به واحد گاز مراجعه نمایید."
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
        />

        {/* Owner Information */}
        <Card
          title={
            <Space>
              <UserOutlined style={{ color: "#1890ff" }} />
              <span>اطلاعات مالک</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16, borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
          headStyle={{ borderBottom: "1px solid #f0f0f0" }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <DetailItem
                icon={<UserOutlined />}
                label="نام و نام خانوادگی مالک"
                value={rowData?.landlordName}
              />
            </Col>
            {rowData?.ownershipTypeEnum === 2 && (
              <Col xs={24} sm={12} md={8}>
                <DetailItem
                  icon={<BankOutlined />}
                  label="نام شرکت"
                  value={rowData?.companyName}
                />
              </Col>
            )}
            <Col xs={24} sm={12} md={8}>
              <DetailItem
                icon={<IdcardOutlined />}
                label="کدملی"
                value={rowData?.landlordNaCode}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <DetailItem
                icon={<PhoneOutlined />}
                label="شماره تماس"
                value={rowData?.landlordPhoneNumber}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <DetailItem
                icon={<TeamOutlined />}
                label="نوع مالکیت"
                value={t(rowData?.ownershipType)}
                isTag
                color="cyan"
              />
            </Col>
          </Row>
        </Card>

        {/* Building Information */}
        <Card
          title={
            <Space>
              <BuildOutlined style={{ color: "#52c41a" }} />
              <span>مشخصات ساختمان</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16, borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
          headStyle={{ borderBottom: "1px solid #f0f0f0" }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={6}>
              <DetailItem
                icon={<ApartmentOutlined />}
                label="نوع کاربری"
                value={getBuildingTypeLabel(rowData?.buildingTypeEnum)}
                isTag
                color="blue"
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <DetailItem
                icon={<ExpandOutlined />}
                label="زیربنا (متر مربع)"
                value={rowData?.area}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <DetailItem
                icon={<NumberOutlined />}
                label="طبقه"
                value={rowData?.floorNumber}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <DetailItem
                icon={<MailOutlined />}
                label="کد پستی"
                value={rowData?.postalCode}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <DetailItem
                label="نوع لوله کشی"
                value={rowData?.pipingType}
                isTag
                color="geekblue"
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <DetailItem
                label="سریال پکیج"
                value={rowData?.packageSerialNumber}
                isTag
                color="purple"
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <DetailItem
                icon={<CalendarOutlined />}
                label="تاریخ ثبت"
                value={rowData?.solarRegisterDate}
              />
            </Col>
          </Row>
        </Card>

        {/* Location Information */}
        <Card
          title={
            <Space>
              <EnvironmentOutlined style={{ color: "#fa8c16" }} />
              <span>موقعیت جغرافیایی</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16, borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
          headStyle={{ borderBottom: "1px solid #f0f0f0" }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <DetailItem
                icon={<EnvironmentOutlined />}
                label="استان"
                value={location.province}
              />
            </Col>
            <Col xs={24} sm={8}>
              <DetailItem
                icon={<EnvironmentOutlined />}
                label="شهرستان"
                value={location.city}
              />
            </Col>
            <Col xs={24} sm={8}>
              <DetailItem
                icon={<EnvironmentOutlined />}
                label="بخش"
                value={location.section}
              />
            </Col>
            <Col span={24}>
              <div
                style={{
                  padding: "12px 16px",
                  background: "#fafafa",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
              >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <HomeOutlined style={{ marginLeft: 6 }} />
                    آدرس کامل
                  </Text>
                  <Text style={{ fontSize: 13, lineHeight: 1.8 }}>
                    {rowData?.address || "-"}
                  </Text>
                </Space>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Description */}
        {rowData?.des1 && (
          <Card
            title={
              <Space>
                <FileTextOutlined style={{ color: "#722ed1" }} />
                <span>توضیحات</span>
              </Space>
            }
            size="small"
            style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
            headStyle={{ borderBottom: "1px solid #f0f0f0" }}
          >
            <Text style={{ fontSize: 13, lineHeight: 1.8 }}>
              {rowData?.des1}
            </Text>
          </Card>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Project Info Header */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          borderRadius: 8,
          background: "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)",
        }}
      >
        <Row gutter={[16, 8]}>
          <Col xs={12} sm={8} md={6}>
            <Space size={4}>
              <Text type="secondary">نوع لوله کشی:</Text>
              <Tag color="blue">{rowData?.pipingType}</Tag>
            </Space>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Space size={4}>
              <Text type="secondary">مالکیت:</Text>
              <Tag color="cyan">{t(rowData?.ownershipType)}</Tag>
            </Space>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Space size={4}>
              <Text type="secondary">تاریخ ثبت:</Text>
              <Text>{rowData?.solarRegisterDate}</Text>
            </Space>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Space size={4}>
              <Text type="secondary">سریال پکیج:</Text>
              <Tag color="purple">{rowData?.packageSerialNumber || "-"}</Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Edit Form */}
      <Card
        title={
          <Space>
            <BuildOutlined />
            <span>ویرایش اطلاعات پرونده</span>
          </Space>
        }
        size="small"
        style={{ borderRadius: 8 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={!isEditable}
          size={isMobile ? "large" : "middle"}
        >
          <Row gutter={[16, 0]}>
            {/* Owner Name */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="landlordName"
                label="نام و نام خانوادگی مالک"
                rules={[{ required: true, message: "نام مالک الزامی است" }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="نام مالک"
                />
              </Form.Item>
            </Col>

            {/* Company Name (if ownership type is 2) */}
            {rowData?.ownershipTypeEnum === 2 && (
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="companyName"
                  label="نام شرکت"
                  rules={[{ required: true, message: "نام شرکت الزامی است" }]}
                >
                  <Input
                    prefix={<BuildOutlined style={{ color: "#bfbfbf" }} />}
                    placeholder="نام شرکت"
                  />
                </Form.Item>
              </Col>
            )}

            {/* National Code */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="landlordNaCode"
                label="کدملی مالک/شرکت"
                rules={[
                  { required: true, message: "کدملی الزامی است" },
                  { len: 10, message: "کدملی باید ۱۰ رقم باشد" },
                ]}
              >
                <Input
                  prefix={<IdcardOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="کدملی"
                  maxLength={10}
                  style={{ direction: "ltr" }}
                />
              </Form.Item>
            </Col>

            {/* Phone Number */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="landlordPhoneNumber"
                label="شماره تماس"
                rules={[
                  { required: true, message: "شماره تماس الزامی است" },
                  { len: 11, message: "شماره تماس باید ۱۱ رقم باشد" },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="09123456789"
                  maxLength={11}
                  style={{ direction: "ltr" }}
                />
              </Form.Item>
            </Col>

            {/* Postal Code */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="postalCode"
                label="کد پستی"
                rules={[{ required: true, message: "کد پستی الزامی است" }]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="کد پستی ۱۰ رقمی"
                  maxLength={10}
                  style={{ direction: "ltr" }}
                />
              </Form.Item>
            </Col>

            {/* Area */}
            <Col xs={12} sm={8} md={4}>
              <Form.Item
                name="area"
                label="زیربنا (متر مربع)"
                rules={[{ required: true, message: "زیربنا الزامی است" }]}
              >
                <InputNumber
                  min={1}
                  max={10000}
                  style={{ width: "100%" }}
                  placeholder="متراژ"
                />
              </Form.Item>
            </Col>

            {/* Floor Number */}
            <Col xs={12} sm={8} md={4}>
              <Form.Item
                name="floorNumber"
                label="طبقه"
                rules={[{ required: true, message: "طبقه الزامی است" }]}
              >
                <InputNumber
                  max={50}
                  style={{ width: "100%" }}
                  placeholder="طبقه"
                />
              </Form.Item>
            </Col>

            {/* Building Type */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="buildingTypeEnum"
                label="نوع کاربری"
                rules={[{ required: true, message: "نوع کاربری الزامی است" }]}
              >
                <Select placeholder="انتخاب کاربری">
                  <Select.Option value={1}>مسکونی</Select.Option>
                  <Select.Option value={2}>تجاری</Select.Option>
                  <Select.Option value={3}>اداری</Select.Option>
                  <Select.Option value={5}>عمومی</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }}>
            <Text type="secondary">موقعیت جغرافیایی</Text>
          </Divider>

          {/* Location Selection */}
          <Row gutter={[16, 0]}>
            {/* Province */}
            <Col xs={24} sm={8}>
              <Form.Item label="استان">
                <Select
                  value={selectedProvince}
                  onChange={handleProvinceChange}
                  options={provinceOptions}
                  placeholder="انتخاب استان"
                  showSearch
                  optionFilterProp="label"
                  disabled={!isEditable}
                />
              </Form.Item>
            </Col>

            {/* City */}
            <Col xs={24} sm={8}>
              <Form.Item label="شهرستان">
                <Select
                  value={selectedCity}
                  onChange={handleCityChange}
                  options={cityList}
                  placeholder="انتخاب شهرستان"
                  showSearch
                  optionFilterProp="label"
                  disabled={!isEditable || cityList.length === 0}
                />
              </Form.Item>
            </Col>

            {/* Section */}
            <Col xs={24} sm={8}>
              <Form.Item label="بخش">
                <Select
                  value={selectedSection}
                  onChange={handleSectionChange}
                  options={sectionList}
                  placeholder="انتخاب بخش"
                  showSearch
                  optionFilterProp="label"
                  disabled={!isEditable || sectionList.length === 0}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Address */}
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                name="address"
                label="آدرس کامل"
                rules={[{ required: true, message: "آدرس الزامی است" }]}
              >
                <TextArea
                  rows={3}
                  placeholder="آدرس دقیق محل را وارد کنید..."
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* des1 */}
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                name="des1"
                label="توضیحات"
                rules={[{ required: false }]}
              >
                <TextArea
                  rows={3}
                  placeholder="توضیحات اضافی را وارد کنید..."
                  showCount
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Submit Button */}
          {isEditable && (
            <Row>
              <Col span={24}>
                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={loading}
                    size="large"
                    style={{ minWidth: 150 }}
                  >
                    ذخیره تغییرات
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form>
      </Card>
    </div>
  )
}

export default ProjectUpdate
